import React, { useState, useEffect } from 'react';
import { CreditCard, Calendar, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../utils/toast';

interface Subscription {
  id: string;
  calendar: {
    id: string;
    name: string;
    subscription_price_cents: number;
  };
  stripe_subscription_id: string;
  status: string;
  current_period_end: string;
}

export default function SubscriptionManagement() {
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchSubscriptions();
    }
  }, [user]);

  const fetchSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          id,
          calendar:calendars!inner (
            id,
            name,
            subscription_price_cents
          ),
          stripe_subscription_id,
          status,
          current_period_end
        `)
        .eq('user_id', user?.id)
        .in('status', ['active', 'canceled'])
        .not('stripe_subscription_id', 'is', null);

      if (error) throw error;
      setSubscriptions(data || []);
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (subscriptionId: string) => {
    if (!user || cancelling) return;

    setCancelling(subscriptionId);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-subscription`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ subscriptionId })
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to cancel subscription');
      }

      toast.success('Subscription cancelled successfully');
      fetchSubscriptions();
    } catch (err) {
      console.error('Error cancelling subscription:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setCancelling(null);
    }
  };

  if (loading) {
    return <div>Loading subscriptions...</div>;
  }

  if (!subscriptions.length) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">You don't have any active paid subscriptions</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {subscriptions.map(subscription => {
        const isCancelled = subscription.status === 'canceled';
        const periodEnd = new Date(subscription.current_period_end);

        return (
          <div key={subscription.id} className="bg-white rounded-lg shadow-sm p-6 flex items-center justify-between">
            <div>
              <Link 
                to={`/calendar/${subscription.calendar.id}`}
                className="font-medium text-gray-900 hover:text-purple-600"
              >
                {subscription.calendar.name}
              </Link>
              <p className="text-sm text-gray-500">
                ${(subscription.calendar.subscription_price_cents / 100).toFixed(2)}/month
              </p>
              <p className="text-sm text-gray-500">
                {isCancelled ? (
                  <>Access until: {periodEnd.toLocaleDateString()}</>
                ) : (
                  <>Next billing date: {periodEnd.toLocaleDateString()}</>
                )}
              </p>
              {isCancelled && (
                <p className="text-sm text-red-600 mt-1">
                  Subscription will end on {periodEnd.toLocaleDateString()}
                </p>
              )}
            </div>
            {!isCancelled && (
              <button
                onClick={() => handleCancel(subscription.stripe_subscription_id)}
                disabled={cancelling === subscription.stripe_subscription_id}
                className="px-4 py-2 text-red-600 hover:text-red-700 disabled:opacity-50"
              >
                {cancelling === subscription.stripe_subscription_id ? (
                  'Cancelling...'
                ) : (
                  'Cancel'
                )}
              </button>
            )}
            {isCancelled && (
              <span className="px-4 py-2 text-red-600">
                Cancelled
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}